import React, { useRef, useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import _ from 'lodash';

import AppBar from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import Loader from 'components/Loader';
import Pagination from 'components/Pagination';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import SearchWithApi from 'components/SearchWithApi';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { CostCenter } from 'models/costCenter';

import CostCenterEmptyList from './components/CostCenterEmptyList';
import CostCenterItem from './components/CostCenterItem';
import { List, Container, HeaderEdit, SubTitle, LoadingContainer } from './styles';
import OptionsItems from 'components/UserList/Components/OptionsItems';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import useIsMobile from 'hooks/useIsMobile';

interface Props {
  costCenterTarget?: string;
  costCenterList: CostCenter[];
  isLoading: boolean;
  isSaving: boolean;
  tourHasAccess: boolean;
  tourLoading: boolean;
  ruleSelected?: CostCenter;
  onCloseSelectionScreen?: () => void;
  onConfirmSelection?: (costCenter: CostCenter) => void;
  onToggleCostCenter?: (costCenter: CostCenter) => void;
  loadCostCenters?: (currentPage: number, costCenter?: string, status?: string) => void;
  totalCostCenters?: number;
  currentPage?: number;
  maxItemsPerPage?: number;
}

const CostCenterList = ({
  costCenterList,
  isLoading,
  tourHasAccess,
  tourLoading,
  onToggleCostCenter,
  isSaving,
  loadCostCenters,
  currentPage,
  maxItemsPerPage,
  totalCostCenters,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const [savingId, setSavingId] = useState<string>();
  const [query, setQuery] = useState('');
  const statusRef = useRef(null);
  const [statusText, setStatusText] = useState<string>('');
  const [statusValue, setStatusValue] = useState<string>('all');
  const isMobile = useIsMobile();
  
  const handleSelection = (costCenter: CostCenter) => {
    history.push(`/corporate/costCenter/${costCenter.id}`);
  };

  const handleOnSearch = _.debounce((value: string) => {
    setQuery(value);
    loadCostCenters(1, value, statusValue);
  }, 750);

  const handleOnSearchNew = _.debounce((value: string, statusValueParam: string) => {
    const newValue = statusValueParam !== undefined ? query : value;
    setQuery(newValue);
    if (loadCostCenters !== undefined) {
      loadCostCenters(1, newValue, statusValueParam ?? statusValue);
    } else {
      handleOnSearch(newValue);
    }
  }, 750);

  const initialSteps: Step[] = [
    {
      target: '.target',
      content: t('costCenterList.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  const userStatusList: { key: string; value: string }[] = 
  [
    { key: 'all', value: t('usersList.dropdown.all') },
    { key: 'active', value: t('usersList.dropdown.active') },
    { key: 'inactive', value: t('usersList.dropdown.inactive') }
  ];

  const handleSelectStatus = (name: string) => {
    const status = userStatusList.find((g) => g.key === name);
    setStatusText(status?.value ?? '');
    setStatusValue(status?.key ?? 'all');
  };

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.push('/corporate/dashboard')}>
          <h5>{t('costCenterList.title')}</h5>
        </AppBar>
        <SubTitle onClick={() => history.push('/corporate/costCenterReport')}>{t('costCenter.report.title')}</SubTitle>
      </Mobile>

      {isLoading && (
        <LoadingContainer>
          <Loader fullScreen={true} />
        </LoadingContainer>
      )}

      <Desktop>
        <ScreenHeader
          title={t('userMenu.costCenterTitle')}
          subtitle={t('userMenu.costCenterDetail')}
          right={
            <HeaderEdit onClick={() => history.push('/corporate/costCenterReport')}>
              {t('costCenter.report.title')}
            </HeaderEdit>
          }
        />
      </Desktop>

      <Container hasList={costCenterList.length > 0}>
        <Tour
          type="cancelable"
          show={!tourLoading && !tourHasAccess}
          hideArrow
          steps={initialSteps}
          disableOverlayClose
          disableScrollParentFix
        />

        <>
            <div style={ { display: isMobile ? 'block' : 'flex', paddingBottom: '30px' } }>
              <div style={ { flex: 1 } }>
                <SearchWithApi show={true} onSearch={handleOnSearchNew} />
              </div>
              <div style={ { flex: 1, paddingLeft: '16px', maxWidth: isMobile ? '95%' : '200px', display: (isLoading ? 'none' : 'block') } }>
                <CustomSelect
                  name="travelStatus"
                  onChange={handleSelectStatus}
                  afterOnChange={handleOnSearchNew}
                  label=""
                  placeHolder={t('usersList.dropdown.all')}
                  selectRef={statusRef}
                  value={statusText}>
                  {({ ...selectProps }: CustomSelectRenderProps) => (
                    <OptionsItems {...selectProps} selectRef={statusRef} userStatus={userStatusList} />
                  )}
                </CustomSelect>                
              </div>
              <div style={ { flex: 1 } }>
                <></>
              </div>
              <div style={ { flex: 1 } }>
                <></>
              </div>
            </div>  
          {!isLoading && (
            <>
              <List>
                {costCenterList.map((costCenter) => (
                  <CostCenterItem
                    key={`user-${costCenter.id}`}
                    costCenter={costCenter}
                    onClick={() => handleSelection(costCenter)}
                    loading={isSaving && savingId === costCenter.id}
                    onToggle={() => {
                      setSavingId(costCenter.id);
                      if (onToggleCostCenter) onToggleCostCenter(costCenter);
                    }}
                  />
                ))}
              </List>
              <Pagination
                currentPage={currentPage}
                maxItemsPerPage={maxItemsPerPage}
                total={totalCostCenters}
                onClick={(page) => {
                  loadCostCenters !== undefined ? loadCostCenters(page ?? 1, query, statusValue) : null;
                }}
              />
            </>
          )}
        </>

        {costCenterList.length === 0 && <CostCenterEmptyList />}
      </Container>
      <FloatingButton zIndex={2} onClick={() => history.push('/corporate/costCenter')} className="target" />
    </>
  );
};

export default CostCenterList;
